<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { useToast } from 'vue-toast-notification'
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.min.css'
import '@/assets/multiselect.css'
import { useTodoStore } from '@/stores/useTodoStore'
import { useContactStore } from '@/stores/useContactStore'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import { PlusIcon, CheckCircleIcon, XCircleIcon, ArrowTopRightOnSquareIcon, DocumentDuplicateIcon } from '@heroicons/vue/24/outline'
import MicButton from '@/components/common/inputs/MicButton.vue'
import HelpButton from '@/components/common/help/HelpButton.vue'
import { useContextualHelp } from '@/composables/useContextualHelp'

const props = defineProps<{
  todo?: any
  showModal?: boolean
  mode?: 'create' | 'edit' | 'view'
  contact?: any
  company?: any
}>()

const emit = defineEmits<{
  close: []
  created: [todoUuid?: string]
  updated: []
  'complete-and-copy': [data: { title: string; type: string; priority: string; company: any; contact: any; description: string }]
}>()

const $toast = useToast()
const todoStore = useTodoStore()
const contactStore = useContactStore()
const isInternalOpen = ref(false)

useContextualHelp('todo', () => isInternalOpen.value || !!props.showModal)

const todoTypes = [
  { id: 'TASK', value: 'TASK', label: 'Tâche' },
  { id: 'REMINDER', value: 'REMINDER', label: 'Rappel' },
  { id: 'CALL', value: 'CALL', label: 'Appel' },
  { id: 'EMAIL', value: 'EMAIL', label: 'Email' },
  { id: 'MEETING', value: 'MEETING', label: 'Réunion' },
  { id: 'FOLLOW_UP', value: 'FOLLOW_UP', label: 'Suivi' },
]

const todoPriorities = [
  { id: 'LOW', value: 'LOW', label: 'Basse' },
  { id: 'MEDIUM', value: 'MEDIUM', label: 'Moyenne' },
  { id: 'HIGH', value: 'HIGH', label: 'Haute' },
  { id: 'URGENT', value: 'URGENT', label: 'Urgente' },
]

const loading = ref(false)
const companySearchLoading = ref(false)
const contactSearchLoading = ref(false)
const companyOptions = ref<any[]>([])
const contactOptions = ref<any[]>([])

const form = reactive({
  title: '',
  description: '',
  type: 'TASK' as string,
  priority: 'MEDIUM' as string,
  status: 'PENDING' as string,
  dueDate: '',
  company: props.company || null as any,
  contact: props.contact || null as any,
})

const isFormValid = computed(() => {
  return form.title.trim() !== ''
})

// Mode de fonctionnement (create, edit, view)
const currentMode = computed(() => {
  if (props.mode) return props.mode
  if (props.todo) {
    if (props.todo.status === 'COMPLETED' || props.todo.status === 'CANCELLED') {
      return 'view'
    }
    return 'edit'
  }
  return 'create'
})

const isViewMode = computed(() => currentMode.value === 'view')
const isEditMode = computed(() => currentMode.value === 'edit')
const isCreateMode = computed(() => currentMode.value === 'create')

// Peut changer le statut seulement si PENDING
const canChangeStatus = computed(() => {
  return form.status === 'PENDING'
})

const modalTitle = computed(() => {
  if (isViewMode.value) return 'Détails de la tâche'
  if (isEditMode.value) return 'Modifier la tâche'
  return 'Créer une nouvelle tâche'
})

watch(
  () => props.todo,
  (newTodo) => {
    if (newTodo) {
      initializeForm(newTodo)
    }
  },
  { immediate: true },
)
watch(
  () => props.contact,
  (contact) => {
    if (contact) {
      initializeForm({contact: contact})
    }
  },
  { immediate: true },
)

watch(
  () => props.showModal,
  (newValue) => {
    if (newValue !== undefined) {
      isInternalOpen.value = newValue
    }
  },
)

function initializeForm(todo: any) {
  form.title = todo.title || ''
  form.description = todo.description || ''
  form.type = todo.type || 'TASK'
  form.priority = todo.priority || 'MEDIUM'
  form.status = todo.status || 'PENDING'
  form.dueDate = todo.dueDate ? formatDateLocal(todo.dueDate) : ''
  form.company = todo.company || props.company || null
  form.contact = todo.contact || props.contact || null

  // Initialiser les options de recherche avec les valeurs actuelles
  if (todo.company) {
    companyOptions.value = [todo.company]
  }
  if (todo.contact) {
    contactOptions.value = [todo.contact]
  }

}

function formatDateLocal(dateString: string) {
  const date = new Date(dateString)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function handleOpen() {
  isInternalOpen.value = true
}

function handleClose() {
  if (!isCreateMode.value) {
    // Mode edit/view via props
    emit('close')
  } else {
    // Mode create
    resetForm()
    isInternalOpen.value = false
    emit('close')
  }
}

function resetForm() {
  form.title = ''
  form.description = ''
  form.type = 'TASK'
  form.priority = 'MEDIUM'
  form.status = 'PENDING'
  form.dueDate = ''
  form.company = null
  form.contact = null
  companyOptions.value = []
  contactOptions.value = []
}

async function onSearchCompany(query: string) {
  if (query.length < 3) {
    companyOptions.value = form.company ? [form.company] : []
    return
  }
  companySearchLoading.value = true
  try {
    const results = await contactStore.searchCompanies(query)
    companyOptions.value = Array.isArray(results) ? results : []
  } finally {
    companySearchLoading.value = false
  }
}

async function onSearchContact(query: string) {
  if (query.length < 3) {
    contactOptions.value = form.contact ? [form.contact] : []
    return
  }
  contactSearchLoading.value = true
  try {
    const results = await contactStore.searchContacts(query)
    contactOptions.value = Array.isArray(results) ? results : []
  } finally {
    contactSearchLoading.value = false
  }
}

async function handleCreate() {
  if (!isFormValid.value) return

  loading.value = true
  try {
    const payload: any = {
      title: form.title,
      description: form.description || undefined,
      type: form.type,
      priority: form.priority,
      dueDate: form.dueDate || undefined,
    }

    if (form.company?.uuid) {
      payload.companyUuid = form.company.uuid
    }

    if (form.contact?.uuid) {
      payload.contactUuid = form.contact.uuid
    }

    const result = await todoStore.createTodo(payload)

    if (result.ok) {
      $toast.success('Tâche créée avec succès')
      emit('created', result.todoUuid)
      handleClose()
    } else {
      $toast.error(result.errorMessage || 'Erreur lors de la création de la tâche')
    }
  } catch (error: any) {
    $toast.error(error?.message ?? 'Erreur lors de la création de la tâche.')
  } finally {
    loading.value = false
  }
}

async function handleUpdate() {
  if (!isFormValid.value || !props.todo?.uuid) return

  loading.value = true
  try {
    const payload: any = {
      title: form.title,
      description: form.description || undefined,
      type: form.type,
      priority: form.priority,
      status: form.status,
      dueDate: form.dueDate || undefined,
    }

    if (form.company?.uuid) {
      payload.companyUuid = form.company.uuid
    } else {
      payload.companyUuid = null
    }

    if (form.contact?.uuid) {
      payload.contactUuid = form.contact.uuid
    } else {
      payload.contactUuid = null
    }

    const result = await todoStore.updateTodo(props.todo.uuid, payload)

    if (result.ok) {
      $toast.success('Tâche modifiée avec succès')
      emit('updated')
      handleClose()
    } else {
      $toast.error(result.errorMessage || 'Erreur lors de la modification de la tâche')
    }
  } catch (error: any) {
    $toast.error(error?.message ?? 'Erreur lors de la modification de la tâche.')
  } finally {
    loading.value = false
  }
}

async function handleChangeStatus(newStatus: 'COMPLETED' | 'CANCELLED') {
  if (!props.todo?.uuid || !canChangeStatus.value) return

  loading.value = true
  try {
    const payload: any = {
      title: form.title,
      description: form.description || undefined,
      type: form.type,
      priority: form.priority,
      status: newStatus,
      dueDate: form.dueDate || undefined,
    }

    if (form.company?.uuid) {
      payload.companyUuid = form.company.uuid
    } else {
      payload.companyUuid = null
    }

    if (form.contact?.uuid) {
      payload.contactUuid = form.contact.uuid
    } else {
      payload.contactUuid = null
    }

    const result = await todoStore.updateTodo(props.todo.uuid, payload)

    if (result.ok) {
      const statusLabel = newStatus === 'COMPLETED' ? 'terminée' : 'annulée'
      $toast.success(`Tâche ${statusLabel} avec succès`)
      emit('updated')
      handleClose()
    } else {
      $toast.error(result.errorMessage || 'Erreur lors du changement de statut')
    }
  } catch (error: any) {
    $toast.error(error?.message ?? 'Erreur lors du changement de statut.')
  } finally {
    loading.value = false
  }
}

async function handleCompleteAndCopy() {
  if (!props.todo?.uuid || !canChangeStatus.value) return

  loading.value = true
  try {
    const payload: any = {
      title: form.title,
      description: form.description || undefined,
      type: form.type,
      priority: form.priority,
      status: 'COMPLETED',
      dueDate: form.dueDate || undefined,
      companyUuid: form.company?.uuid ?? null,
      contactUuid: form.contact?.uuid ?? null,
    }

    const result = await todoStore.updateTodo(props.todo.uuid, payload)

    if (result.ok) {
      $toast.success('Tâche terminée avec succès')
      emit('complete-and-copy', {
        title: form.title,
        type: form.type,
        priority: form.priority,
        company: form.company,
        contact: form.contact,
        description: form.description,
      })
    } else {
      $toast.error(result.errorMessage || 'Erreur lors du changement de statut')
    }
  } catch (error: any) {
    $toast.error(error?.message ?? 'Erreur lors du changement de statut.')
  } finally {
    loading.value = false
  }
}

function getStatusBadgeClass(status: string) {
  switch (status) {
    case 'COMPLETED':
      return 'bg-emerald-100'
    case 'CANCELLED':
      return 'bg-red-100'
    default:
      return 'bg-blue-100'
  }
}

function getStatusLabel(status: string) {
  const labels: Record<string, string> = {
    PENDING: 'En attente',
    COMPLETED: 'Terminé',
    CANCELLED: 'Annulé',
  }
  return labels[status] || status
}
</script>

<template>
  <!-- Bouton pour créer (mode create seulement) -->
  <ButtonDaisy v-if="isCreateMode && !showModal" text="Créer une tâche" variant="primary" :icon="PlusIcon" @click="handleOpen" />

  <dialog class="modal" :class="{ 'modal-open': isInternalOpen || showModal }">
    <div class="modal-box max-w-2xl p-0 overflow-hidden">
      <div class="p-4 border-b bg-base-200/50 flex justify-between items-center">
        <div class="flex items-center gap-3">
          <div class="flex items-center gap-1">
            <h3 class="text-lg font-bold">{{ modalTitle }}</h3>
            <HelpButton />
          </div>
          <span v-if="!isCreateMode" class="badge badge-sm" :class="getStatusBadgeClass(form.status)">
            {{ getStatusLabel(form.status) }}
          </span>
        </div>
        <button class="btn btn-sm btn-circle btn-ghost" @click="handleClose">✕</button>
      </div>

      <div class="p-6 max-h-[80vh] overflow-y-auto">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
          <!-- Titre -->
          <div class="col-span-1 md:col-span-2">
            <label class="block text-xs font-bold uppercase text-base-content/60 mb-1">Titre *</label>
            <input
              v-model="form.title"
              type="text"
              placeholder="Titre de la tâche"
              class="input input-bordered input-sm w-full"
              :disabled="loading || isViewMode"
              required
            />
          </div>

          <!-- Type -->
          <div>
            <label class="block text-xs font-bold uppercase text-base-content/60 mb-1">Type</label>
            <select v-model="form.type" class="select select-bordered select-sm w-full" :disabled="loading || isViewMode">
              <option v-for="type in todoTypes" :key="type.id" :value="type.value">
                {{ type.label }}
              </option>
            </select>
          </div>

          <!-- Priorité -->
          <div>
            <label class="block text-xs font-bold uppercase text-base-content/60 mb-1">Priorité</label>
            <select v-model="form.priority" class="select select-bordered select-sm w-full" :disabled="loading || isViewMode">
              <option v-for="priority in todoPriorities" :key="priority.id" :value="priority.value">
                {{ priority.label }}
              </option>
            </select>
          </div>

          <!-- Date d'échéance -->
          <div class="col-span-1 md:col-span-2">
            <label class="block text-xs font-bold uppercase text-base-content/60 mb-1">Date d'échéance</label>
            <input v-model="form.dueDate" type="date" class="input input-bordered input-sm w-full" :disabled="loading || isViewMode" />
          </div>

          <!-- Entreprise -->
          <div class="col-span-1 md:col-span-2">
            <div class="flex items-center gap-2 mb-1">
              <label class="block text-xs font-bold uppercase text-base-content/60">Entreprise</label>
              <a
                v-if="form.company?.referenceExterne"
                :href="`/partner/${form.company.referenceExterne}`"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center gap-0.5 text-xs text-primary hover:opacity-70"
              >
                Voir la fiche
                <ArrowTopRightOnSquareIcon class="w-3 h-3" />
              </a>
            </div>
            <Multiselect
              v-model="form.company"
              :options="companyOptions"
              :searchable="true"
              :loading="companySearchLoading"
              :internal-search="false"
              placeholder="Rechercher une entreprise..."
              label="name"
              track-by="uuid"
              @search-change="onSearchCompany"
              :disabled="loading || isViewMode || form.contact !== null"
            >
              <template #noOptions>
                <div class="p-2 text-xs">Saisissez au moins 3 caractères</div>
              </template>
              <template #noResult>
                <div class="p-2 text-xs">Aucun résultat trouvé</div>
              </template>
            </Multiselect>
          </div>

          <!-- Contact -->
          <div class="col-span-1 md:col-span-2">
            <div class="flex items-center gap-2 mb-1">
              <label class="block text-xs font-bold uppercase text-base-content/60">Contact</label>
              <a
                v-if="form.contact?.uuid"
                :href="`/dashboard/contacts/${form.contact.uuid}`"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center gap-0.5 text-xs text-primary hover:opacity-70"
              >
                Voir la fiche
                <ArrowTopRightOnSquareIcon class="w-3 h-3" />
              </a>
            </div>
            <Multiselect
              v-model="form.contact"
              :options="contactOptions"
              :searchable="true"
              :loading="contactSearchLoading"
              :internal-search="false"
              placeholder="Rechercher un contact..."
              track-by="uuid"
              :disabled="loading || isViewMode || form.company !== null"
              @search-change="onSearchContact"
            >
              <template #option="{ option }">
                <div>{{ option.firstname }} {{ option.lastname }} - {{ option.company.name }}</div>
              </template>
              <template #singleLabel="{ option }">
                <span>{{ option.firstname }} {{ option.lastname }} - {{ option.company.name }}</span>
              </template>
              <template #noOptions>
                <div class="p-2 text-xs">Saisissez au moins 3 caractères</div>
              </template>
              <template #noResult>
                <div class="p-2 text-xs">Aucun résultat trouvé</div>
              </template>
            </Multiselect>
          </div>

          <!-- Description -->
          <div class="col-span-1 md:col-span-2">
            <div class="flex items-center gap-1 mb-1">
              <label class="block text-xs font-bold uppercase text-base-content/60">Description</label>
              <MicButton :disabled="loading || isViewMode" @result="val => form.description = form.description ? form.description + ' ' + val : val" />
            </div>
            <textarea
              v-model="form.description"
              placeholder="Description de la tâche"
              class="textarea textarea-bordered w-full h-24"
              :disabled="loading || isViewMode"
            />
          </div>
        </div>
      </div>

      <div class="modal-action p-4 border-t bg-base-200/50 mt-0">
        <div class="flex w-full justify-between items-center">
          <!-- Boutons de changement de statut (seulement si PENDING) -->
          <div v-if="!isCreateMode && canChangeStatus" class="flex gap-2">
            <button class="btn btn-sm btn-success" @click="handleChangeStatus('COMPLETED')" :disabled="loading">
              <CheckCircleIcon class="h-4 w-4" />
              Terminer
            </button>
            <button class="btn btn-sm btn-success btn-outline" @click="handleCompleteAndCopy" :disabled="loading">
              <DocumentDuplicateIcon class="h-4 w-4" />
              Terminer et Copier
            </button>
            <button class="btn btn-sm btn-error" @click="handleChangeStatus('CANCELLED')" :disabled="loading">
              <XCircleIcon class="h-4 w-4" />
              Annuler
            </button>
          </div>
          <div v-else></div>

          <!-- Boutons d'action principaux -->
          <div class="flex gap-2">
            <button class="btn btn-sm" @click="handleClose" :disabled="loading">Fermer</button>
            <button v-if="isCreateMode" class="btn btn-sm btn-primary" @click="handleCreate" :disabled="!isFormValid || loading">
              <span v-if="loading" class="loading loading-spinner loading-xs"></span>
              Créer
            </button>
            <button v-if="isEditMode" class="btn btn-sm btn-primary" @click="handleUpdate" :disabled="!isFormValid || loading">
              <span v-if="loading" class="loading loading-spinner loading-xs"></span>
              Modifier
            </button>
          </div>
        </div>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop">
      <button @click="handleClose">Fermer</button>
    </form>
  </dialog>
</template>

<style>
/* Multiselect styling to match the project */
.multiselect__tags {
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 0.5rem !important;
}
</style>
